<template>
  <div class="graph-controls">
    <div class="search-container">
      <b-field>
        <b-input
          v-model="searchQuery"
          type="search"
          icon="magnify"
          placeholder="Search"
          expanded
          class="no-border-input" />
      </b-field>
    </div>
    <div class="toggle-container">
      <span class="toggle-label">Show:</span>
      <div class="toggle-buttons">
        <button
          class="button is-small toggle-button"
          @click="showAll">
          All
        </button>
        <button
          class="button is-small toggle-button"
          @click="hideAll">
          None
        </button>
      </div>
      <b-dropdown
        v-if="type === 'bar'"
        v-model="sortOptionModel"
        position="is-bottom-left"
        class="ml-auto sort-dropdown">
        <template #trigger>
          <button class="button is-small sort-button">
            <span> Sort </span>
          </button>
        </template>

        <b-dropdown-item
          v-for="option in sortOptions"
          :key="option.key"
          :value="option">
          {{ option.label }}
        </b-dropdown-item>
      </b-dropdown>
    </div>
    <div class="filter-container">
      <template v-if="isLoading">
        <div
          v-for="n in 5"
          :key="n"
          class="filter-item-skeleton">
          <b-skeleton
            width="100%"
            height="40px" />
        </div>
      </template>
      <template v-else-if="type === 'bar'">
        <div
          v-for="bar in searchResults"
          :key="bar.id"
          class="filter-item"
          @click="toggleItem(bar.id)">
          <div
            class="filter-item-content"
            :class="{ 'filter-item-active': bar.isActive }">
            <span
              class="color-circle"
              :style="{ backgroundColor: getItemColor(bar) }" />
            <span class="filter-label">{{ bar.label }}</span>
            <i class="material-icons-outlined visibility-icon">
              {{ bar.isActive ? "visibility" : "visibility_off" }}
            </i>
            <span class="filter-value">{{ bar.values }}</span>
          </div>
        </div>
      </template>
      <template v-else-if="type === 'line'">
        <div
          v-for="line in searchResults"
          :key="line.id"
          class="filter-item"
          :class="{ 'filter-item-active': line.isActive }"
          @click="toggleItem(line.id)">
          <div
            class="filter-item-content"
            :class="{ 'filter-item-active': line.isActive }">
            <span
              class="color-circle"
              :style="{ backgroundColor: getItemColor(line) }" />
            <span class="filter-label">{{ line.label }}</span>
            <i class="material-icons-outlined visibility-icon">
              {{ line.isActive ? "visibility" : "visibility_off" }}
            </i>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
  import FuzzySearch from 'fuzzy-search'

  export default {
    props: {
      value: {
        type: Array,
        default: () => [],
      },
      type: {
        type: String,
        required: true,
      },
      isLoading: {
        type: Boolean,
        required: true,
      },
      sortOptions: {
        type: Array,
        required: true,
      },
      sortOption: {
        type: Object,
        required: false,
      },
      hasAverage: {
        type: Boolean,
        default: false,
      },
    },
    data () {
      return {
        searchQuery: '',
        searcher: null,
      }
    },
    computed: {
      searchResults () {
        return this.searcher
          ? this.searcher.search(this.searchQuery)
          : this.value
      },
      sortOptionModel: {
        get () {
          return this.sortOption
        },
        set (sortOption) {
          this.$emit('update:sortOption', sortOption)
        },
      },
    },
    watch: {
      value: {
        handler () {
          this.updateSearcher()
        },
        immediate: true,
      },
      isLoading: {
        handler () {
          if (!this.isLoading && this.sortOption) {
            this.resort()
          }
        },
        immediate: true,
      },
      sortOption: {
        handler () {
          if (!this.isLoading && this.sortOption) {
            this.resort()
          }
        },
        immediate: true,
      },

    },
    methods: {
      getItemValue (item) {
        if (item.bars) {
          return item.bars[0]?.value
        }
        return item.value
      },
      resort () {
        const [field, direction,] = this.sortOption.key.split('_')
        const sortedItems = [...this.value,].sort((a, b) => {
          if (field === 'label') {
            if (direction === 'asc') {
              return a.sort_by.toString().localeCompare(b.sort_by.toString())
            }
            return b.sort_by.toString().localeCompare(a.sort_by.toString())
          } else {
            if (direction === 'asc') {
              return this.getItemValue(a) - this.getItemValue(b)
            }
            return this.getItemValue(b) - this.getItemValue(a)
          }
        })
        this.$emit('input', sortedItems)
      },
      toggleItem (id) {
        const index = this.value.findIndex((item) => item.id === id)
        if (index !== -1) {
          const updatedItems = [...this.value,]
          updatedItems[index] = {
            ...updatedItems[index],
            isActive: !updatedItems[index].isActive,
          }
          this.$emit('input', updatedItems)
        }
      },
      showAll () {
        const updatedItems = this.value.map((item) => ({
          ...item,
          isActive: true,
        }))
        this.$emit('input', updatedItems)
      },
      hideAll () {
        const updatedItems = this.value.map((item) => ({
          ...item,
          isActive: false,
        }))
        this.$emit('input', updatedItems)
      },
      updateSearcher () {
        this.searcher = new FuzzySearch(this.value, ['label',], {
          caseSensitive: false,
        })
      },
      getItemColor (item) {
        if (item?.conditional_quality === 'good') {
          return '#008C84'
        }
        if (item?.conditional_quality === 'bad') {
          return '#d31b5e'
        }
        if (this.hasAverage) {
          return '#c4c4c4'
        }
        return item?.color
      },
    },
  }
</script>

<style scoped lang="scss">
.graph-controls {
  height: 100%;
  display: flex;
  flex-direction: column;
  border-left: 1px solid #e0e0e0;
  background-color: white;
}

.search-container {
  padding: 4px;
  border-bottom: 1px solid #e0e0e0;
}

.no-border-input {
  ::v-deep .input {
    border: none;
    box-shadow: none !important;
  }
}

.filter-container {
  flex-grow: 1;
  overflow-y: auto;
  max-height: 500px;
  overflow-y: auto;

  @include mobile {
    max-height: 100%;
  }
}

.filter-item,
.filter-item-skeleton {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 12px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #f5f5f5;
    .filter-item-content {
      opacity: 1;
      .visibility-icon {
        opacity: 1 !important;
      }
    }
  }
}

.sort-button {
  padding: 4px;
  box-shadow: none !important;
  outline: none !important;
  background-color: white;
  border: 1px solid #e0e0e0 !important;
}

.filter-item-content {
  display: flex;
  align-items: center;
  opacity: 0.5;
  width: 100%;

  .visibility-icon {
    opacity: 1;
  }

  &.filter-item-active {
    opacity: 1;
    .visibility-icon {
      opacity: 0;
    }
  }
}

.color-circle {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 8px;
}

.filter-label {
  font-size: 14px;
}

.filter-value {
  color: #666;
  font-size: 14px;
}

.toggle-container {
  display: flex;
  align-items: center;
  padding: 5px 12px;
  border-bottom: 1px solid #e0e0e0;
}

.toggle-label {
  font-size: 14px;
  color: #999;
  margin-right: 8px;
}

.toggle-buttons {
  display: flex;
  gap: 8px;
  border: none;
}

.toggle-button {
  font-size: 13px !important;
  padding: 0px 8px;
  background-color: #fff;
  color: #888 !important;
  transition: all 0.3s ease;
  border: none !important;
  box-shadow: none !important;

  &:hover {
    background-color: #f0f0f0;
  }
}

.visibility-icon {
  margin-left: auto;
  margin-right: 8px;
  color: #666;
  font-size: 16px;
  opacity: 0.5;
}

.filter-item-skeleton {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

::v-deep .dropdown-item.is-active {
  background-color: #E9E9EA !important;
  color: #28272B !important;
}
</style>
